import { Colors, Icon, Intent } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'
import _ from 'lodash'
import React from 'react'
import ReactDOM from 'react-dom'
import Dropzone from 'react-dropzone'

import apis from 'browser/app/models/apis'
import { Button } from 'browser/components/atomic-elements/atoms/button/button'
import { MasterDetail } from 'browser/components/atomic-elements/atoms/master-detail/master-detail'
import { Column, Table } from 'browser/components/atomic-elements/atoms/table'
import {
  TransientEntityDetailCard,
  toEntityModel,
} from 'browser/components/atomic-elements/organisms/entity/transient-entity-detail-card/transient-entity-detail-card'
import { Query } from 'shared-libs/models/query'

const PAYLOAD_CONFIG_PATH = 'core_integrations_ingestion_multiEntity_payloadConfig'
const SFTP_CONFIG_ENTITY_TYPE =
  '/1.0/entities/metadata/core_integrations_sftp_ingestion_config.json'

// The backend only ever receives the sample text; pasting, dropping, and browsing all feed it.
const SOURCE_PASTE = 'PASTE'

interface ITestPayloadConfigProps {
  entity: any
}

interface ITestPayloadConfigState {
  sample: string
  fileName: string
  isRunning: boolean
  result: any
  error: string
}

function tryParseJson(text: any): any {
  if (typeof text !== 'string') {
    return undefined
  }
  try {
    return JSON.parse(text)
  } catch {
    return undefined
  }
}

/**
 * Pulls the server's error detail out of a rejected request. The API layer merges the parsed
 * response body ({ statusCode, errors: [{ message }] }) onto the Error, so the useful message lives
 * in `err.errors` (worker path) or `err.responseJSON` / `err.responseText` (jQuery path) — not in
 * `err.message`, which is just "HTTP error 400".
 */
function errorDetail(err: any): string | undefined {
  const fromErrors = (arr: any): string | undefined =>
    Array.isArray(arr) && arr.length
      ? arr.map((e) => (typeof e === 'string' ? e : e?.message)).filter(Boolean).join('\n')
      : undefined
  const parsed = tryParseJson(err?.responseText)
  return (
    fromErrors(err?.errors) ||
    fromErrors(err?.responseJSON?.errors) ||
    fromErrors(parsed?.errors) ||
    (typeof err?.responseText === 'string' && err.responseText.trim() ? err.responseText : undefined)
  )
}

/** Combines the HTTP status header with the server's detail message for display. */
function formatRunError(err: any): string {
  const status = err?.statusCode ?? err?.status
  const header = err?.message || (status ? `HTTP error ${status}` : 'Test run failed.')
  const detail = errorDetail(err)
  return detail && detail !== header ? `${header}\n${detail}` : header
}

/** One produced entity, flattened out of its result row for the table view. */
interface IDryRunItem {
  key: string
  rowNum: number
  alias: string
  action: string
  valid: boolean
  validationErrors: any[]
  entity: any
  title: string
}

/** A row-level problem (a hard error, or an alias the config skipped) surfaced above the table. */
interface IDryRunIssue {
  rowNum: number
  label: string
  message: string
}

/** Flattens result.rows[].entities[] into one table item per produced entity. */
function flattenItems(result: any): IDryRunItem[] {
  const items: IDryRunItem[] = []
  _.forEach(result?.rows || [], (row: any) => {
    _.forEach(row.entities || [], (ar: any, idx: number) => {
      const model = toEntityModel(ar.entity)
      const displayName = model?.displayName ? String(model.displayName) : ''
      items.push({
        key: `${row.rowNum}:${ar.alias}:${idx}`,
        rowNum: row.rowNum,
        alias: ar.alias,
        action: ar.action,
        valid: ar.valid,
        validationErrors: ar.validationErrors || [],
        entity: ar.entity,
        title: displayName || ar.alias,
      })
    })
  })
  return items
}

/** Collects per-row errors and skipped aliases so they still show once the cards are gone. */
function collectIssues(result: any): IDryRunIssue[] {
  const issues: IDryRunIssue[] = []
  _.forEach(result?.rows || [], (row: any) => {
    _.forEach(row.errors || [], (err: any) =>
      issues.push({ rowNum: row.rowNum, label: 'error', message: err?.message ?? String(err) })
    )
    _.forEach(_.toPairs(row.skips || {}), ([alias, message]) =>
      issues.push({ rowNum: row.rowNum, label: `skipped ${alias}`, message: message as string })
    )
  })
  return issues
}

const ALIAS_CHIP_STYLE: React.CSSProperties = {
  fontFamily: 'monospace',
  fontWeight: 600,
  background: '#e6edf7',
  color: '#1c4587',
  padding: '1px 8px',
  borderRadius: 10,
}

function ValidBadge({ valid }: { valid: boolean }) {
  return (
    <span
      className={valid ? undefined : 'c-errorColor'}
      style={{ fontWeight: 600, color: valid ? '#0f7b3f' : undefined, whiteSpace: 'nowrap' }}
    >
      {valid ? '✓ valid' : '✗ invalid'}
    </span>
  )
}

/**
 * Full-viewport overlay for the maximized results. Portaled to document.body so it clears the app
 * nav/header (z-index up to ~1000) and their stacking contexts, which would otherwise clip a fixed
 * element rendered inside the detail card.
 */
const MAXIMIZED_OVERLAY_STYLE: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 2000,
  background: '#fff',
  padding: 16,
}

// Default width (px) of the detail pane in the split view; the divider is draggable from here.
const DETAIL_DEFAULT_WIDTH = 520
// Height (px) of the inline (non-maximized) split area. MasterDetail's SplitPane absolutely positions
// its panes, so the wrapper needs a definite height to fill. Maximized fills the overlay instead.
const INLINE_SPLIT_HEIGHT = 480

/**
 * Dry-run results as a table (Row · Entity · Name · Action · Status), rendered with the app's shared
 * `Table` atom so it matches every other list in the product. Clicking a row opens a split view —
 * the table on the left, the selected entity's detail on the right — mirroring the entity
 * list/detail layout. "Maximize" pops the whole results area into a full-viewport overlay (portaled
 * to body so it clears the app's stacking contexts).
 */
class DryRunResults extends React.Component<
  { result: any },
  {
    selectedKey: string | null
    isMaximized: boolean
    containerWidth: number
    containerHeight: number
  }
> {
  // The divider is the app's shared MasterDetail (react-split-pane), left uncontrolled — the same way
  // the entity list/detail view (script:entityListView) uses it. That works because the master pane's
  // fixed-data-table is absolutely positioned (see renderTablePane), so its pixel width never pins the
  // flex pane's min-content and the divider drags freely. We measure that pane with a ResizeObserver
  // and feed the table its width/height, mirroring how the app's EntityTable is sized inside a Card.
  private resizeObserver: ResizeObserver | null = null
  private containerNode: HTMLDivElement | null = null
  private measureRaf = 0

  constructor(props) {
    super(props)
    this.state = {
      selectedKey: null,
      isMaximized: false,
      containerWidth: 0,
      containerHeight: 0,
    }
  }

  public componentWillUnmount() {
    this.resizeObserver?.disconnect()
    if (this.measureRaf) {
      window.cancelAnimationFrame(this.measureRaf)
    }
  }

  public componentDidUpdate(prevProps) {
    // A fresh run replaces the result object; drop any stale selection.
    if (prevProps.result !== this.props.result) {
      this.setState({ selectedKey: null })
    }
  }

  // Re-measure on the next frame — NOT synchronously inside the ResizeObserver callback, to avoid the
  // observe→setState→resize feedback loop the browser throttles.
  private scheduleMeasure = () => {
    if (this.measureRaf) {
      return
    }
    this.measureRaf = window.requestAnimationFrame(() => {
      this.measureRaf = 0
      const node = this.containerNode
      if (!node) {
        return
      }
      const width = node.clientWidth
      const height = node.clientHeight
      const next: { containerWidth?: number; containerHeight?: number } = {}
      if (width && width !== this.state.containerWidth) {
        next.containerWidth = width
      }
      if (height && height !== this.state.containerHeight) {
        next.containerHeight = height
      }
      if (!_.isEmpty(next)) {
        this.setState(next as Pick<DryRunResults['state'], 'containerWidth' | 'containerHeight'>)
      }
    })
  }

  private setContainerRef = (node: HTMLDivElement | null) => {
    this.resizeObserver?.disconnect()
    this.resizeObserver = null
    this.containerNode = node
    if (!node) {
      return
    }
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(this.scheduleMeasure)
      this.resizeObserver.observe(node)
    }
    this.scheduleMeasure()
  }

  private getColumns(isSplit: boolean): Column[] {
    // formatter columns render into the shared Table without needing an Entity/schema/renderer.
    const columns: Column[] = [
      { path: 'rowNum', label: 'Row', width: 56 },
      {
        path: 'alias',
        label: 'Entity',
        width: 120,
        formatter: (alias: string) => <span style={ALIAS_CHIP_STYLE}>{alias}</span>,
      },
      { path: 'title', label: 'Name', width: 200, flexGrow: 1, isPrimary: true },
      { path: 'action', label: 'Action', width: 90 },
      {
        path: 'valid',
        label: 'Status',
        width: 90,
        // Icon-only in the cell — the full "valid/invalid" wording is shown in the detail header.
        formatter: (valid: boolean) => (
          <span style={{ color: valid ? '#0f7b3f' : '#c23030', fontWeight: 600 }}>
            {valid ? '✓' : '✗'}
          </span>
        ),
      },
    ]
    // The split-view table pane is narrow; drop the Action column to keep Name legible.
    return isSplit ? columns.filter((c) => c.path !== 'action') : columns
  }

  public render() {
    const { result } = this.props
    const items = flattenItems(result)
    const issues = collectIssues(result)
    const selectedItem = items.find((i) => i.key === this.state.selectedKey) || null
    const body = this.renderBody(items, issues, selectedItem)

    if (this.state.isMaximized) {
      // Full-viewport overlay, portaled to body so it isn't clipped by the detail card's stacking
      // context (see MAXIMIZED_OVERLAY_STYLE). The Restore button lives in the body's header row.
      return ReactDOM.createPortal(
        <div className='u-flexColumn' style={MAXIMIZED_OVERLAY_STYLE}>
          {body}
        </div>,
        document.body
      )
    }
    return <div className='u-bumperTop'>{body}</div>
  }

  private renderBody(
    items: IDryRunItem[],
    issues: IDryRunIssue[],
    selectedItem: IDryRunItem | null
  ) {
    const { result } = this.props
    return (
      <>
        <div className='u-flex u-alignItems--center u-bumperBottom--sm'>
          <span>
            {result.rowCount} row(s), {result.failureCount} failed.
          </span>
          <Button
            style={{ marginLeft: 'auto' }}
            rightIcon={this.state.isMaximized ? IconNames.MINIMIZE : IconNames.MAXIMIZE}
            onClick={() => this.setState((prev) => ({ isMaximized: !prev.isMaximized }))}
          />
        </div>
        {issues.length > 0 && this.renderIssues(issues)}
        {selectedItem ? this.renderSplit(items, selectedItem) : (
          <div ref={this.setContainerRef} style={{ minWidth: 0, overflow: 'hidden' }}>
            {this.renderTable(items, false)}
          </div>
        )}
      </>
    )
  }

  /**
   * Table (left) + selected entity detail (right) with a draggable divider — the app's shared
   * MasterDetail (react-split-pane), same as the entity list/detail view (script:entityListView).
   * MasterDetail's SplitPane absolutely positions its panes, so the wrapper needs a definite height: a
   * fixed height inline, or fill the maximized overlay. `detailPanelMaxSize={-360}` reserves a minimum
   * width for the table (negative == container width − 360).
   */
  private renderSplit(items: IDryRunItem[], selectedItem: IDryRunItem) {
    const wrapperStyle: React.CSSProperties = this.state.isMaximized
      ? { display: 'flex', flex: '1 1 auto', minHeight: 0 }
      : { display: 'flex', height: INLINE_SPLIT_HEIGHT }
    return (
      <div style={wrapperStyle}>
        <MasterDetail
          showDetailPanel={true}
          detailPanelDefaultSize={DETAIL_DEFAULT_WIDTH}
          detailPanelMinSize={320}
          detailPanelMaxSize={-360}
          detailPanelSplit='vertical'
          masterPanel={this.renderTablePane(items)}
          detailPanel={this.renderDetailPanel(selectedItem)}
        />
      </div>
    )
  }

  /**
   * The detail pane: the dry-run-specific framing (alias chip + action + valid badge + close, and any
   * validation errors) over a reusable TransientEntityDetailCard. Keyed by the selected item so the
   * card remounts and re-resolves on selection change. flex:1 + minWidth:0 fills the pane — the shared
   * `.Pane` class makes MasterDetail's pane a flex row, so without it the panel would sit at its
   * content width and stop short of the pane's right edge as the divider is dragged.
   */
  private renderDetailPanel(item: IDryRunItem) {
    return (
      <div
        key={item.key}
        className='u-flexColumn'
        style={{ height: '100%', flex: 1, minWidth: 0 }}
      >
        <div
          className='u-flex u-alignItems--center'
          style={{ padding: '6px 10px', borderBottom: '1px solid #e0e0e0', background: '#f4f6f8' }}
        >
          <span style={ALIAS_CHIP_STYLE}>{item.alias}</span>
          <span className='u-bumperLeft--xs' style={{ fontWeight: 600 }}>
            {item.action}
          </span>
          <span className='u-bumperLeft--sm'>
            <ValidBadge valid={item.valid} />
          </span>
          <Button
            style={{ marginLeft: 'auto' }}
            rightIcon={IconNames.CROSS}
            onClick={() => this.setState({ selectedKey: null })}
          />
        </div>
        {item.validationErrors.length > 0 && (
          <ul className='c-errorColor' style={{ margin: 0, padding: '6px 10px 6px 28px' }}>
            {item.validationErrors.map((err, i) => (
              <li key={i}>
                {err.field ? `${err.field}: ` : ''}
                {err.message}
              </li>
            ))}
          </ul>
        )}
        <TransientEntityDetailCard content={item.entity} />
      </div>
    )
  }

  /**
   * The master pane: an absolutely-positioned table inside a measured, relatively-positioned box. The
   * absolute table is out of flow, so it contributes nothing to the pane's min-content — that is what
   * lets the (uncontrolled) divider shrink and grow the pane freely. The ResizeObserver on the box
   * feeds the table its width/height as the pane resizes. (Same shape as the app's EntityTable.)
   */
  private renderTablePane(items: IDryRunItem[]) {
    return (
      <div
        ref={this.setContainerRef}
        style={{ position: 'relative', flex: 1, minWidth: 0, overflow: 'hidden' }}
      >
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}>
          {this.renderTable(items, true)}
        </div>
      </div>
    )
  }

  private renderIssues(issues: IDryRunIssue[]) {
    return (
      <ul className='c-placeholderColor u-bumperBottom--sm'>
        {issues.map((issue, i) => (
          <li key={i}>
            <strong>Row {issue.rowNum}</strong> — {issue.label}: {issue.message}
          </li>
        ))}
      </ul>
    )
  }

  private renderTable(items: IDryRunItem[], isSplit: boolean) {
    if (items.length === 0) {
      return <div className='c-placeholderColor u-innerBumper'>No entities produced.</div>
    }
    const selectedItem = items.find((i) => i.key === this.state.selectedKey)
    // Both views measure the container the table lives in: the master pane in the split view, the
    // full-width row otherwise. Split view fills the pane's height too; the full-width view caps at a
    // fixed height and grows with content. maxHeight is a cap, so few rows still render short.
    const width = this.state.containerWidth
    const maxHeight = isSplit
      ? this.state.containerHeight || INLINE_SPLIT_HEIGHT
      : this.state.isMaximized ? 900 : 440
    return (
      <Table
        frames={undefined}
        columns={this.getColumns(isSplit)}
        rows={items}
        keyPath={(row: any) => row.key}
        isSelectable={false}
        enableMultiSelect={false}
        selection={selectedItem ? [selectedItem] : []}
        onRowClick={(_selection: any, row: any) => this.setState({ selectedKey: row.key })}
        width={width || undefined}
        maxHeight={maxHeight}
        rowHeight={37}
      />
    )
  }
}

/**
 * Dry-run sandbox for a Multi Entity Ingestion Payload Config. Runs the draft config (as currently
 * edited, unsaved) against a pasted sample or an uploaded CSV file (read into `sample` client-side)
 * through the real ingestion loop without writing anything, and shows, per row, the entities the
 * config would produce, their create/update action, and validation, each rendered as an entity
 * preview alongside its raw JSON.
 *
 * Tests the payload config alone — no SFTP ingestion config is needed. Munging (the pre-processor)
 * is driven entirely by the payload config's property map, so the "Run munging pre-processor"
 * option still works; the server derives the firm from the session.
 *
 * Placement-agnostic: rendered as the "Dry Run" tab on the payload config's detail card. Gating
 * (super-admin + payload-config mixin) is applied by the caller.
 */
export class TestPayloadConfig extends React.Component<
  ITestPayloadConfigProps,
  ITestPayloadConfigState
> {
  // SFTP config id sent to satisfy the (still-deployed) endpoint; see resolveIngestionConfigId.
  // Hard-coded to a known wv3 config as a temporary bridge until the config-optional server ships.
  private cachedConfigId?: string = '2c8f7d2c-2060-4b2e-ab11-6f0ea9d8e7af'

  private dropzone?: Dropzone

  constructor(props: ITestPayloadConfigProps) {
    super(props)
    this.state = {
      sample: '',
      fileName: '',
      isRunning: false,
      result: null,
      error: '',
    }
  }

  public render() {
    return (
      <div className='u-innerBumper'>
        <div
          className='u-flex u-alignItems--center u-bumperBottom--sm'
          style={{
            gap: 8,
            padding: '10px 12px',
            borderRadius: 6,
            background: Colors.LIGHT_GRAY5,
            border: `1px solid ${Colors.LIGHT_GRAY2}`,
          }}
        >
          <Icon icon={IconNames.INFO_SIGN} color={Colors.GRAY2} />
          <span className='c-placeholderColor'>
            Dry run — nothing is written. Shows the entities each input row would produce.
          </span>
        </div>

        {this.renderSourceInput()}

        <div className='u-flex u-alignItems--center u-bumperTop--sm' style={{ gap: 12 }}>
          <Button
            intent={Intent.PRIMARY}
            isDisabled={!this.canRun()}
            onClick={this.handleRun}
          >
            {this.state.isRunning ? 'Running…' : 'Run'}
          </Button>
          {this.state.sample.length > 0 && !this.state.isRunning && (
            <a className='c-placeholderColor' onClick={this.handleClear}>
              Clear
            </a>
          )}
        </div>

        {this.renderResult()}
      </div>
    )
  }

  // One control for all three gestures: paste into the textarea, drop a CSV on it, or browse.
  // disableClick keeps clicks on the textarea (cursor/selection); the file picker opens only via "browse".
  private renderSourceInput() {
    const { sample, fileName } = this.state
    return (
      <div>
        <div className='u-flex u-alignItems--baseline u-bumperBottom--xs' style={{ gap: 8 }}>
          <span style={{ fontWeight: 600 }}>Sample payload</span>
          <span className='c-placeholderColor' style={{ fontSize: 12 }}>
            CSV — a header row of <code>alias.path</code> columns, then one row per record
          </span>
        </div>

        <Dropzone
          ref={(ref) => (this.dropzone = ref ?? undefined)}
          accept='.csv,text/csv,text/plain'
          multiple={false}
          disableClick={true}
          disablePreview={true}
          onDrop={this.handleDrop}
          className='u-block'
          activeClassName='is-active'
        >
          {({ isDragActive }) => (
            <div
              style={{
                position: 'relative',
                borderRadius: 6,
                border: `2px dashed ${isDragActive ? Colors.BLUE3 : Colors.LIGHT_GRAY1}`,
                background: isDragActive ? Colors.BLUE5 : Colors.WHITE,
                transition: 'border-color 120ms, background 120ms',
                padding: 4,
              }}
            >
              <div
                className='u-flex u-alignItems--center'
                style={{
                  gap: 6,
                  padding: '6px 8px',
                  fontSize: 12,
                  color: Colors.GRAY1,
                  borderBottom: `1px solid ${Colors.LIGHT_GRAY3}`,
                }}
              >
                <Icon icon={IconNames.CLOUD_UPLOAD} color={Colors.GRAY2} iconSize={14} />
                <span>
                  Drag a CSV file here, paste below, or{' '}
                  <a onClick={() => this.dropzone?.open()}>browse</a>
                </span>
              </div>
              <textarea
                className='c-input'
                spellCheck={false}
                style={{
                  width: '100%',
                  minHeight: 150,
                  border: 'none',
                  boxShadow: 'none',
                  resize: 'vertical',
                  padding: '8px',
                  background: 'transparent',
                  fontFamily: 'Menlo, Monaco, Consolas, monospace',
                  fontSize: 12,
                  lineHeight: 1.5,
                }}
                value={sample}
                placeholder={'appt.name,appt.trailer\nA123,TRL-9'}
                onChange={(e) => this.setState({ sample: e.target.value, fileName: '' })}
              />
              {isDragActive && (
                <div
                  className='u-flex u-alignItems--center u-justifyContent--center'
                  style={{
                    position: 'absolute',
                    inset: 0,
                    borderRadius: 6,
                    background: 'rgba(45, 114, 210, 0.06)',
                    color: Colors.BLUE2,
                    fontWeight: 600,
                    pointerEvents: 'none',
                  }}
                >
                  <Icon icon={IconNames.IMPORT} color={Colors.BLUE2} />
                  <span style={{ marginLeft: 8 }}>Drop to load the file</span>
                </div>
              )}
            </div>
          )}
        </Dropzone>

        {fileName ? (
          <div
            className='u-flex u-alignItems--center u-bumperTop--xs'
            style={{ gap: 6, fontSize: 12, color: Colors.GREEN2 }}
          >
            <Icon icon={IconNames.TICK_CIRCLE} color={Colors.GREEN3} iconSize={14} />
            <span>
              Loaded <strong>{fileName}</strong> — {sample.length.toLocaleString()} chars
            </span>
          </div>
        ) : (
          <div className='c-placeholderColor u-bumperTop--xs' style={{ fontSize: 12 }}>
            {sample ? `${sample.length.toLocaleString()} chars` : ' '}
          </div>
        )}
      </div>
    )
  }

  private handleClear = () => {
    this.setState({ sample: '', fileName: '', result: null, error: '' })
  }

  private handleDrop = (accepted: File[]) => {
    const file = accepted && accepted[0]
    if (!file) {
      return
    }
    const reader = new FileReader()
    reader.onload = () => this.setState({ sample: String(reader.result || ''), fileName: file.name })
    reader.readAsText(file)
  }

  private canRun(): boolean {
    const { sample, isRunning } = this.state
    if (isRunning) {
      return false
    }
    // Both remaining sources (paste, upload) run off the sample text.
    return _.trim(sample).length > 0
  }

  private handleRun = async () => {
    const { entity } = this.props
    const { sample } = this.state
    const payloadConfig = entity.get(PAYLOAD_CONFIG_PATH) ?? _.get(entity.content, PAYLOAD_CONFIG_PATH)

    this.setState({ isRunning: true, result: null, error: '' })

    try {
      const ingestionConfigId = await this.resolveIngestionConfigId()
      // An uploaded file is just a pasted sample the backend never sees as a file, so both sources
      // run the PASTE path. The munging pre-processor always runs — testing the property map is the
      // whole point of the dry run.
      const result = await apis.testIngestionPayloadConfig({
        ingestionConfigId,
        payloadConfig,
        source: SOURCE_PASTE,
        sample,
        runPreProcessor: true,
      })
      const parsed = _.isString(result) ? JSON.parse(result) : result
      this.setState({ isRunning: false, result: parsed })
    } catch (err) {
      this.setState({ isRunning: false, error: formatRunError(err) })
    }
  }

  // The deployed dry-run endpoint still requires a real SFTP ingest config to resolve the firm, even
  // though a payload-only test doesn't otherwise use it. Auto-pick any config for the current firm
  // so the user never has to choose one. Cached for the component's lifetime.
  // TODO(VD-24597): drop once the config-optional server change is deployed to all environments.
  private resolveIngestionConfigId = async (): Promise<string> => {
    if (this.cachedConfigId) {
      return this.cachedConfigId
    }
    const collection = new Query(apis)
      .setEntityType(SFTP_CONFIG_ENTITY_TYPE)
      .setSize(1)
      .getCollection()
    await collection.find()
    const first = _.get(collection, 'result.children[0]')
    const id = first?.uniqueId ?? first?.entityId
    if (!id) {
      throw new Error('No SFTP ingestion config exists for this firm to run the dry run against.')
    }
    this.cachedConfigId = id
    return id
  }

  private renderResult() {
    const { result, error } = this.state
    if (error) {
      return <pre className='u-bumperTop c-errorColor'>{error}</pre>
    }
    if (!result) {
      return null
    }
    return <DryRunResults result={result} />
  }
}
